// URL routing: the app's location as a citable address.
//
// Every one of these sites arrived independently at the same need — link to a
// chapter, a witness page, a word — and each hand-rolled it, in a different
// shape (path segments here, query params there, hash state elsewhere). This
// is the machinery, not the grammar: each site declares its own routes and
// keeps the URLs it already publishes.
//
// The model
// ---------
// A ROUTE is a flat plain object, `{name, ...fields}`, that fully describes
// what the app is showing. parse() turns a URL into one; build() turns one back
// into a URL. They are inverses over canonical input, which is what makes a URL
// safe to write on every state change: build(parse(u)) === u.
//
// A site supplies patterns, each naming a path template and the query params
// that may ride along:
//
//   createRouter({
//     routes: [
//       {name: 'witness',  path: '/w/:witness[/p:page][/l:line][/w:word]',
//        query: {layer: {default: 'htr'}, sok: {type: 'bool'}}},
//       {name: 'synopsis', path: '/synopsis/:par[/b:block]',
//        types: {par: 'int', block: 'int'}},
//     ],
//     fallback: {name: 'witness'},
//   });
//
// Path syntax: `:name` is a required field, `[...]` a group that is present
// only when every field inside it is set. Groups nest left-to-right, so a
// route cannot skip a level — /w/x/l3 with no page is not expressible, which
// is correct: a line number means nothing without its page.
//
// A field matches `[^/]+` by default. Where two fields are ADJACENT in a
// segment that default is ambiguous and silently wrong — mishnah's
// `ch:ch:part-:view` over "ch12-text" splits as ch=1, part=2 — so such a field
// must declare its own shape in `patterns`:
//
//   {path: '/tractate/:slug[/ch:ch:part-:view]',
//    patterns: {ch: '\\d+', part: '[a-z]?'}}
//
// which then reads ch=12, part='' correctly. A field whose pattern can match
// nothing is optional WITHIN its group: `:part?` says so on the build side too,
// so /tractate/sukkah/ch4-align round-trips with no part rather than dropping
// the whole group because one field of it is absent.
//
// Only non-default query values are written, so equivalent states always
// produce the same string and the common URL stays short.

const isSet = v => v !== undefined && v !== null && v !== '';

// ── field types ──────────────────────────────────────────────────
// A type converts between the string in the URL and the value in the route.
// `list` keeps commas literal: they are legal in a query and a citation with
// %2C in it is unreadable.
const TYPES = {
    string: {parse: s => s, build: v => String(v)},
    int: {parse: s => { const n = parseInt(s, 10); return Number.isFinite(n) ? n : null; },
          build: v => String(v)},
    bool: {parse: s => s === '1' || s === 'true',
           build: v => (v ? '1' : '0')},
    list: {parse: s => String(s).split(',').filter(Boolean),
           build: v => (Array.isArray(v) ? v.join(',') : String(v))},
};

const typeOf = (name) => TYPES[name] || TYPES.string;

// ── path templates ───────────────────────────────────────────────
// Compiled once per route into a matching regex plus the field order, so
// parse() is a single exec rather than a hand-written matcher per site.
function compile(template, patterns) {
    const fields = [];
    const pat = patterns || {};
    let re = '', i = 0;
    // Walk the template, emitting regex. `[` opens an optional group, which
    // becomes a non-capturing optional group in the regex; everything inside
    // it is therefore optional together.
    const walk = (stop) => {
        let out = '';
        while (i < template.length) {
            const c = template[i];
            if (c === stop) { i++; return out; }
            if (c === '[') { i++; out += '(?:' + walk(']') + ')?'; continue; }
            if (c === ':') {
                i++;
                let n = '';
                while (i < template.length && /[A-Za-z0-9_]/.test(template[i])) n += template[i++];
                if (template[i] === '?') i++;        // optional marker: build-side only
                fields.push(n);
                out += '(' + (pat[n] || '[^/]+') + ')';
                continue;
            }
            out += c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            i++;
        }
        return out;
    };
    re = walk(null);
    return {re: new RegExp('^' + re + '/?$'), fields};
}

// Render a template from a route object, dropping any optional group whose
// fields are not all set.
function render(template, route, types) {
    let i = 0;
    const walk = (stop) => {
        let out = '', ok = true;
        while (i < template.length) {
            const c = template[i];
            if (c === stop) { i++; break; }
            if (c === '[') {
                i++;
                const inner = walk(']');
                if (inner.ok) out += inner.out;   // a dropped group drops its nesting too
                continue;
            }
            if (c === ':') {
                i++;
                let n = '';
                while (i < template.length && /[A-Za-z0-9_]/.test(template[i])) n += template[i++];
                const optional = template[i] === '?';
                if (optional) i++;
                if (!isSet(route[n])) {
                    // An optional field contributes nothing but does not
                    // invalidate the group around it.
                    if (!optional) ok = false;
                    continue;
                }
                out += encodeURIComponent(typeOf(types[n]).build(route[n]));
                continue;
            }
            out += c;
            i++;
        }
        return {out, ok};
    };
    return walk(null).out;
}

export function createRouter(spec) {
    const win = spec.window || (typeof window !== 'undefined' ? window : null);
    const routes = (spec.routes || []).map(r => ({
        ...r, types: r.types || {}, query: r.query || {},
        ...compile(r.path, r.patterns),
    }));
    const byName = Object.fromEntries(routes.map(r => [r.name, r]));

    // Where the site is served from. When the shell is opened directly (file://
    // or /web/index.html on a dumb host) clean paths are not being rewritten,
    // so the route rides in ?r= instead and every link still works.
    const basePath = spec.basePath || '';
    const routed = () => (spec.routed != null ? spec.routed : !basePath);

    function parse(href) {
        const loc = win ? win.location : {href: 'http://x/', origin: 'http://x'};
        const u = new URL(href || loc.href, loc.origin || 'http://x');
        const q = u.searchParams;
        let path = routed() ? u.pathname : (q.get('r') || '');
        if (basePath && path.indexOf(basePath) === 0) path = path.slice(basePath.length);
        path = path.replace(/\/+$/, '') || '/';

        for (const r of routes) {
            const m = r.re.exec(path);
            if (!m) continue;
            const out = {name: r.name};
            r.fields.forEach((f, k) => {
                const raw = m[k + 1];
                // '' comes from an optional sub-pattern that matched nothing
                // (part in `[a-z]?`); that is absence, not an empty value.
                out[f] = (raw === undefined || raw === '') ? null
                       : typeOf(r.types[f]).parse(decodeURIComponent(raw));
            });
            for (const [key, opt] of Object.entries(r.query)) {
                const param = opt.param || key;
                out[key] = q.has(param)
                    ? typeOf(opt.type).parse(q.get(param))
                    : (opt.default !== undefined ? opt.default
                       : (opt.type === 'bool' ? false : (opt.type === 'list' ? [] : null)));
            }
            return out;
        }
        return {...(spec.fallback || {name: routes[0] && routes[0].name})};
    }

    function build(route) {
        const r = byName[route.name] || routes[0];
        if (!r) return basePath || '/';
        let path = render(r.path, route, r.types);
        // Ordered, and commas left literal: URLSearchParams percent-encodes
        // them, which turns a readable with=a,b into with=a%2Cb.
        const parts = [];
        const add = (k, v) => parts.push(k + '=' + encodeURIComponent(v).replace(/%2C/g, ','));
        for (const [key, opt] of Object.entries(r.query)) {
            const v = route[key];
            if (!isSet(v) && !(opt.type === 'bool' && v === false)) continue;
            const t = typeOf(opt.type);
            const s = t.build(v);
            if (opt.default !== undefined && s === t.build(opt.default)) continue;
            if (opt.type === 'bool' && opt.default === undefined && v === false) continue;
            if (opt.type === 'list' && (!v || !v.length)) continue;
            add(opt.param || key, s);
        }
        if (!routed()) { parts.unshift('r=' + encodeURIComponent(path)); path = basePath; }
        const qs = parts.join('&');
        return (path || '/') + (qs ? '?' + qs : '');
    }

    // ── history ──────────────────────────────────────────────────
    // push() for a move the reader would expect Back to undo; replace() for
    // refinement that should not pile up entries. A push whose URL already sits
    // in the bar is still a real navigation — the app typically replaces its
    // way there while loading, then commits — so it is collapsed only when the
    // ROUTE is unchanged too.
    let current = null, pushed = null;
    function go(route, isReplace) {
        const url = build(route);
        const loc = win.location;
        const here = loc.pathname + loc.search;
        current = route;
        if (spec.title) {
            const t = spec.title(route);
            if (t) win.document.title = t;
        }
        const same = url === here && JSON.stringify(route) === JSON.stringify(pushed);
        if (isReplace || same) win.history.replaceState(route, '', url);
        else { win.history.pushState(route, '', url); pushed = route; }
    }

    return {
        parse, build,
        push: r => go(r, false),
        replace: r => go(r, true),
        current: () => current,
        // Absolute form, for a copy-link affordance: always the clean-path URL
        // so a link copied from a dev server still reads as a real citation.
        permalink(route) {
            const loc = win.location;
            return new URL(build(route), loc.origin).href;
        },
        // Fires on Back/Forward with the parsed route.
        onPopState(fn) {
            win.addEventListener('popstate', () => fn(parse()));
        },
    };
}
